import type { CaresAboutSectionProps } from './CaresAboutSection.types';
import { Badge, Card, CardTitle, CardContent, type BadgeProps } from '@monksflow/monks-ui';
import { ThumbsDownIcon, ThumbsUpIcon, type LucideIcon } from 'lucide-react';
import { SectionHeader } from '@/shared/ui/SectionHeader';
import { useTranslation } from 'react-i18next';

/* Where they stand today is stated plainly, and where the brand wants them is the one
   coloured card of the pair - the contrast is the point of putting them side by side.
   The colour carries the badge and the fill only; the prose underneath reads the same
   everywhere, so a card's tint never changes how legible its content is.

   Tessra's Badge has no blue variant, so the desired-perception badge is the one that
   states its own fill. Raised with design rather than resolved here. */
const CARD_STYLES = {
  currentPerception: { bgColor: 'bg-surface', badgeVariant: 'default' },
  desiredPerception: {
    bgColor: 'bg-blue-50',
    badgeVariant: 'secondary',
    badgeClass: 'bg-blue-100 text-blue-800',
  },
  likes: { bgColor: 'bg-green-50', badgeVariant: 'positive', icon: ThumbsUpIcon },
  dislikes: { bgColor: 'bg-red-50', badgeVariant: 'destructive', icon: ThumbsDownIcon },
} as const satisfies Record<
  string,
  { bgColor: string; badgeVariant: BadgeProps['variant']; badgeClass?: string; icon?: LucideIcon }
>;

type CardStyleKey = keyof typeof CARD_STYLES;

interface RelationshipCardProps {
  styleKey: CardStyleKey;
  content: string;
  label: string;
}

const RelationshipCard = ({ styleKey, content, label }: RelationshipCardProps) => {
  const style = CARD_STYLES[styleKey];
  const badgeClass = 'badgeClass' in style ? style.badgeClass : undefined;
  const Icon = 'icon' in style ? style.icon : undefined;

  return (
    <Card variant="default" className="flex-1 shadow-none gap-4 type-body-md p-0">
      <CardTitle className="p-0">
        <Badge
          variant={style.badgeVariant}
          roundness="full"
          // Badge paints its icons `text-foreground-subtle`, which would leave a
          // grey thumb in a green or red pill. `text-current` hands the icon the
          // colour the variant already chose for the label beside it.
          className={`[&_svg]:text-current ${badgeClass ?? ''}`}
        >
          {Icon && <Icon aria-hidden />}
          {label}
        </Badge>
      </CardTitle>
      <CardContent className="text-foreground-alt p-0">{content}</CardContent>
    </Card>
  );
};

export const CaresAboutSection = ({ persona }: CaresAboutSectionProps) => {
  const { t } = useTranslation('audiences');

  const brandRelationship = persona.insightsData?.data.brandRelationship;

  if (!brandRelationship) {
    return null;
  }

  const { perception, product } = brandRelationship;

  return (
    <section className="flex flex-col gap-6">
      <SectionHeader title={t('audienceFlowWidget.caresAboutSection.caresAbout')} />

      <div className="flex flex-col gap-3">
        {(perception.current || perception.desired) && (
          <Card variant="outline" className="flex flex-col shadow-none border-border-light">
            <CardTitle className="px-6">
              <SectionHeader
                title={t('audienceFlowWidget.caresAboutSection.brandRelationship')}
                size="small"
              />
            </CardTitle>
            <CardContent className="flex flex-col md:flex-row gap-6">
              {perception.current && (
                <RelationshipCard
                  styleKey="currentPerception"
                  label={t('audienceFlowWidget.caresAboutSection.currentPerception')}
                  content={perception.current}
                />
              )}
              {perception.desired && (
                <RelationshipCard
                  styleKey="desiredPerception"
                  label={t('audienceFlowWidget.caresAboutSection.desiredRelationship')}
                  content={perception.desired}
                />
              )}
            </CardContent>
          </Card>
        )}

        {(product.likes || product.dislikes) && (
          <Card variant="outline" className="flex flex-col shadow-none border-border-light">
            <CardTitle className="px-6">
              <SectionHeader
                title={t('audienceFlowWidget.caresAboutSection.productRelationship')}
                size="small"
              />
            </CardTitle>
            <CardContent className="flex flex-col md:flex-row gap-6">
              {product.likes && (
                <RelationshipCard
                  styleKey="likes"
                  label={t('audienceFlowWidget.caresAboutSection.whatTheyLike')}
                  content={product.likes}
                />
              )}
              {product.dislikes && (
                <RelationshipCard
                  styleKey="dislikes"
                  label={t('audienceFlowWidget.caresAboutSection.whatTheyDislike')}
                  content={product.dislikes}
                />
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </section>
  );
};
